import {EeviForm, EeviFormState} from "../../../../common/web_common/forms/eevi_form";
import {React, withRouter} from "../../../../common/web_common/components/eevi_react_exports";
import {apiV1, EeviBaseContainer, MenuLink} from "../../../../common/web_common/containers/eevi_std_container";
import {EeviApi} from "../../../../common/web_common/components/eevi_api";
import {eeviId} from "../../../../common/web_common/components/eevi_util";
import {
    AlarmHeader,
    AlarmTable,
    CarerAvailabilityData, playTone,
    RealtimeEvent,
    RealtimeEventCompute
} from "../components/alarms_components";
import {dynamicSubclass} from "../../../../common/web_common/components/eevi_transform";
import {LoginCredentials} from "../../../../common/web_common/components/eevi_data";
import {EeviCoreContext} from "../../../../common/web_common/core/components/eevi_core_context";
import {CareGroup, ZoneLocation} from "../../../../common/web_common/core/eevi_core_data";
import {eeviGlobal} from "../../../../common/web_common/components/eevi_context";
import {getDemoCarerAvailable, getDemoEvents} from "../components/demo/sample_data";
import {QUERY_WINDOW} from "../components/alarms_components";

const DEFAULT_REFRESH_RATE = 5000; //ms
const MIN_REFRESH_RATE = 1000; //ms
const DEFAULT_SOUND_INTERVAL = 60000; //ms

class AlarmsFormState implements EeviFormState{
    loading?: boolean;
    nextToken?: string;
    redirect?: string;
    title?: string;
    windowHeight: number = 0;
    windowWidth: number = 0;
    error: any;
    requestId?: string;

    events: RealtimeEvent[] = [];

    // If set to true then do not show the Resident name on screen.  Default is false (to show)..
    hideResidentName: boolean = false;

    // The field to sort on (default for current), and if ascending (default A-Z, etc) or descending
    sortField: string = '';
    sortAscendingOrder: boolean = true;

    currentDateTime: Date = new Date();
    lastUpdate: Date = new Date();

    availableCarers = new Map<string, CarerAvailabilityData>();
    zoneLocations: ZoneLocation[] = [];
    selectedZones: string[] = [];
    soundAutoplayBlocked: boolean = false;

    constructor(events: RealtimeEvent[]) {
        this.events = events
    }
}

export class AlarmsForm extends EeviForm<any, AlarmsFormState>{
    private readonly api: EeviApi<AlarmsFormState>;
    private pageVisible = false;  // is this page visible?
    private mounted = false;
    private requestId = '';  // pagination
    private _timers = new Map();  // polling data periodically

    // Visibility Handler stops api invocation if the page is not visible
    private _visibilityHandler: VoidFunction | undefined;
    private _soundUnlockHandler: EventListener | undefined;
    private soundUnlockInProgress = false;
    private soundUnlockAttemptId = 0;
    private alarmTonePlayStarting = false;
    private soundUnlockExplicitlyFailed = false;
    private interruptedPrimeAudios = new Set<HTMLMediaElement>();
    private unlockedAlarmAudios = new Set<HTMLMediaElement>();

    private demoMode: number | undefined = undefined;

    // Audible alerts run as two independent engines:
    //  - new-arrival announcement: fires the tick an unattended event first appears
    //  - persistence reminder: a soundInterval timer that re-beeps while any
    //    unattended event (or carer alarm) is still active.
    // `tracked` is the set of currently unattended events (eventId -> priority) -
    // the "still active" memory both engines read.
    private tracked = new Map<string, number>();
    private pendingNewArrivalAnnouncements = new Map<string, {priority: number, reanchor: boolean}>();
    private carerAlarm: boolean = false;   // derived from carer availability data

    // access core context (i.e. carer group, name,...)
    static contextType = EeviCoreContext;  // Use EeviCoreContext to get user's CareGroups

    // new notification audio
    private notification_audio = document.getElementById("new-event-notification") as HTMLMediaElement
    private p1_notification_audio = document.getElementById("new-event-p1-notification") as HTMLMediaElement

    private refreshRate = DEFAULT_REFRESH_RATE;
    private soundInterval = DEFAULT_SOUND_INTERVAL;
    private handleSelectedZonesChange: React.Dispatch<React.SetStateAction<string[]>> = (value) => {
        this.setState((prevState) => ({
            selectedZones: typeof value === "function" ? value(prevState.selectedZones) : value
        }));
    };

    constructor(props: any) {
        super(props, "Event Status", false, true);
        this.api = EeviApi.fromComponent<AlarmsFormState>(this);
        this.state = new AlarmsFormState([]);
        this.render = this.render.bind(this);
    }

    protected menuLinks(): MenuLink[] {
        return [];
    }

    private showCarerAvailability(demoMode:number | undefined): boolean {
        // We use the read_permissions as a sort of user default - e.g. a permission
        // indicates we must do something.
        //
        // If we have demo mode enabled, then we always show carer availability.
        // This is not a security issue because we use hard coded data for demo mode.
        // The only way to see real data is to have the permission.
        return (
            eeviGlobal.loggedInUser.read_permissions.has("carer_availability") ||
            this.demoMode!=undefined
        );
    }

    private loadSmartAnnunciatorSettings() {
        this.api.get<{refresh_rate_seconds: number | null, sound_interval_seconds: number | null}>(
            `${apiV1}/smart_annunciator_settings`,
            undefined,
            (data) => {
                const settings = data as {refresh_rate_seconds: number | null, sound_interval_seconds: number | null}
                const refreshSec = settings?.refresh_rate_seconds
                const soundSec = settings?.sound_interval_seconds
                this.refreshRate = Math.max(
                    MIN_REFRESH_RATE,
                    (typeof refreshSec === "number" && refreshSec > 0) ? refreshSec * 1000 : DEFAULT_REFRESH_RATE
                )
                this.soundInterval =
                    (typeof soundSec === "number" && soundSec > 0) ? soundSec * 1000 : DEFAULT_SOUND_INTERVAL
                if (this._timers.has("events")) {
                    this.register_timer("events", () => { this.retrieveRecentEvents() }, this.refreshRate)
                }
                // The carers timer is registered in loadCareGroups before this
                // response arrives, so it starts with the default refreshRate.
                // Re-register it here so admin settings slow it down too.
                if (this._timers.has("carers")) {
                    this.register_timer("carers", () => { this.retrieveCarerLoggedIn() }, this.refreshRate*2)
                }
                // Persistence reminder is registered in initialize() with the
                // default soundInterval; re-register so admin settings take effect.
                if (this._timers.has("persistence")) {
                    this.register_timer("persistence", () => { this.persistenceTick() }, this.soundInterval)
                }
            }
        )
    }

    private loadCareGroups() {
        console.log("Loading care groups...");
        this.context.model.careGroups = "loading";
        this.api.get<CareGroup[]>(
            `${apiV1}/care_groups`,
            undefined,
            (data) => {
                this.context.model.careGroups = data as CareGroup[];

                this.loadSmartAnnunciatorSettings()

                let parameters = new URLSearchParams(window.location.search)
                this.setState({
                    loading:false,
                    hideResidentName: this.getHideResidentName(parameters),
                    sortField: this.getSortFieldName(parameters),
                    sortAscendingOrder: this.getSortOrderAscending(parameters),
                })

                // Refered alarm_components.tsx ln 476 careGroups number is 1 in real world village use case.
                // So not async is used here but definitely can do if needed.
                this.loadZoneLocations()

                // Timer for carers currently logged in, polling period is 1 minute
                // If we have carer_availability permission, or we have demoMode set then
                // get carer availability info.  We check demoMode as we want to ensure we show
                // the hard coded carer availability when we have demo enabled (as this is our test mode).
                if (this.showCarerAvailability(this.demoMode)){
                    this.retrieveCarerLoggedIn()
                    this.register_timer(
                        "carers",
                        () => {
                            this.retrieveCarerLoggedIn();
                        },
                        this.refreshRate*2
                    )
                }
            },
            undefined,
            true,
            (error) => {this.errorHandler(error)}
        )
    }

    private addVisibilityHandler(){
        if (typeof document.hidden !== "undefined") {
            if (!this._visibilityHandler) {
                this._visibilityHandler = () => {
                    this.pageVisible = !document.hidden
                }
                document.addEventListener("visibilitychange", this._visibilityHandler)
            }
        } else {
            console.warn("Current browser does not support Page Visibility")
        }
    }

    private addSoundUnlockHandler(){
        if (!this._soundUnlockHandler) {
            this._soundUnlockHandler = () => {
                this.unlockAlarmAudio()
            }
            this.soundUnlockEvents().forEach((eventName) => {
                document.addEventListener(eventName, this._soundUnlockHandler!, {capture: true, passive: true})
            })
        }
    }

    private removeSoundUnlockHandler(){
        if (this._soundUnlockHandler) {
            this.soundUnlockEvents().forEach((eventName) => {
                document.removeEventListener(eventName, this._soundUnlockHandler!, {capture: true})
            })
            this._soundUnlockHandler = undefined
        }
    }

    private soundUnlockEvents(): string[] {
        return ["keydown", "pointerdown", "mousedown", "touchstart", "click"]
    }

    private loadZoneLocations() {
        for (const care_group of this.context.model.careGroups) {
            if (!care_group.careGroupCode) {break}
            this.api.get<ZoneLocation[]>(
                `${apiV1}/village/${care_group.careGroupCode}/zones`,
                undefined,
                (zoneLocations) => {
                    this.setState((prevState) => {
                        const merged = new Map<string, ZoneLocation>();
                        prevState.zoneLocations.forEach((zone) => {
                            merged.set(zone.zoneCode, zone);
                        });
                        (zoneLocations as ZoneLocation[]).forEach((zone) => {
                            merged.set(zone.zoneCode, zone);
                        });
                        return {
                            ...prevState,
                            zoneLocations: Array.from(merged.values())
                        };
                    });
                }
            );
        }
    }

    private removeVisibilityHandler(){
        if(this._visibilityHandler){
            document.removeEventListener("visibilitychange", this._visibilityHandler)
            this._visibilityHandler = undefined
        }
    }

    private initialize(){
        this.pageVisible = true;

        // Add visible handler
        this.addVisibilityHandler()

        // Get recent events
        this.retrieveRecentEvents(true)

        // Setting up a polling timer that fires every refreshRate to get a list of open events
        this.register_timer(
            "events",
            ()=>{
                this.retrieveRecentEvents()
            },
            this.refreshRate
        )

        // Persistence reminder: re-beeps every soundInterval while any unattended
        // event (or carer alarm) is still active. New arrivals re-anchor it.
        this.register_timer("persistence", () => { this.persistenceTick() }, this.soundInterval)
    }

    protected retrieveCarerLoggedIn(){
        for (const care_group of this.context.model.careGroups) {
            if (!care_group.careGroupCode) {break}
            this.api.get<CarerAvailabilityData>(
                `${apiV1}/events_status/carers_available?care_group_code=` + care_group.careGroupCode,
                undefined,
                (data) => {
                    let parameters = new URLSearchParams(window.location.search)
                    this.setState({
                        hideResidentName: this.getHideResidentName(parameters),
                        sortField: this.getSortFieldName(parameters),
                        sortAscendingOrder: this.getSortOrderAscending(parameters),
                    })

                    if (this.demoMode){
                        // Quick and dirty demo
                        const retrievedVillage = getDemoCarerAvailable(this.demoMode).map(
                            (v) => [v.careGroupName, v] as [string, CarerAvailabilityData]
                        )
                        const demoCarers = new Map(retrievedVillage)
                        this.updateCarerAlarm(demoCarers)
                        this.setState({availableCarers: demoCarers,
                            loading: false,
                            error: null,
                        })
                    } else {
                        if (data) {
                            const carers = this.state.availableCarers.set(care_group.careGroupName, data as CarerAvailabilityData)
                            this.updateCarerAlarm(carers)
                            this.setState({
                                ...this.state,
                                availableCarers: carers,
                                loading: false,
                                error: null,
                            })
                        }
                    }
                },
                undefined,
                false,
                (error) => {
                    this.errorHandler(error)
                }
            )
        }

    }

    private register_timer(name: string, callback: CallableFunction, ms: number = 1000){
        // Single call point for all timers
        const existing = this._timers.get(name)
        if (existing !== undefined) {
            clearInterval(existing)
        }
        this._timers.set(
            name,
            setInterval(
                () => {
                    if (this.pageVisible) {
                        callback()
                    }
                }, ms
            )
        )
    }

    // @ts-ignore
    /*private remove_timer(name: string){
        if (this._timers.has(name)) {
            clearInterval(this._timers.get(name))
            this._timers.delete(name)
        }else{
            console.log("Unable to find timer:" + name)
        }
    }*/

    private clear_timers(){
        this._timers.forEach(
            (value, key) => {
                clearInterval(value)
                this._timers.delete(key)
                console.log(`${key} was removed from timer list`)
            }
        )
    }

    // Derive the carer alarm straight from the availability data so it is set
    // and cleared deterministically by the carer poll, independent of rendering
    // and of the event poll's alarm clearing.
    private updateCarerAlarm(carers: Map<string, CarerAvailabilityData>){
        let alarm = false
        carers.forEach((data) => {
            if (
                data
                && data.config.carer_availability_message
                && data.config.carer_availability_alarm
                && data.totalAvailableCarers === 0
            ) {
                alarm = true
            }
        })
        // Announce the moment carers drop to zero (false -> true); the 60s
        // persistence timer keeps reminding afterwards. Carer alarm has no
        // priority, so it always uses the standard tone. Only re-anchor the
        // persistence timer when this standard-priority alarm is not below an
        // already active tracked event.
        if (alarm && !this.carerAlarm && this.pageVisible) {
            this.playAlarmTone(100)
            const alreadyActivePriority = this.highestTrackedPriority()
            if (100 <= alreadyActivePriority) {
                this.register_timer("persistence", () => { this.persistenceTick() }, this.soundInterval)
            }
        }
        this.carerAlarm = alarm
    }

    private highestTrackedPriority(): number {
        let priority = 100
        this.tracked.forEach((p) => { priority = Math.min(priority, p) })
        return priority
    }

    // Persistence reminder: re-beep while any unattended event (or carer alarm)
    // is still active. Registered as the "persistence" timer at soundInterval.
    private persistenceTick(){
        if (this.tracked.size > 0) {
            this.playAlarmTone(this.highestTrackedPriority())
        } else if (this.carerAlarm) {
            this.playAlarmTone(100)  // carer reminder has no priority -> standard tone
        }
        // else: nothing active -> stay idle
    }

    // Single point that selects the tone by priority and plays it. The <audio>
    // tags start muted (see index.html); unmute before playing. Whether play()
    // is allowed is decided by the browser autoplay policy (has the user
    // interacted), which is what the soundAutoplayBlocked warning tracks.
    private playAlarmTone(priority: number, onPlayed: VoidFunction = () => {}){
        const audioToPlay = priority === 1 ? this.p1_notification_audio : this.notification_audio
        this.alarmTonePlayStarting = true
        if (this.soundUnlockInProgress) {
            this.interruptedPrimeAudios.add(audioToPlay)
            this.invalidateSoundUnlockAttempt()
        }
        audioToPlay.muted = false
        audioToPlay.volume = 1
        playTone(
            audioToPlay,
            () => {
                this.alarmTonePlayStarting = false
                if (!this.mounted) {
                    return
                }
                console.warn("Autoplay failed. User interaction is required!")
                this.unlockedAlarmAudios.delete(audioToPlay)
                this.soundUnlockExplicitlyFailed = true
                this.setState({soundAutoplayBlocked: true})
                this.addSoundUnlockHandler()
            },
            () => {
                this.alarmTonePlayStarting = false
                if (!this.mounted) {
                    return
                }
                this.markAlarmAudioUnlocked(audioToPlay)
                this.soundUnlockExplicitlyFailed = false
                this.refreshSoundAutoplayBlockedForUnlockState()
                onPlayed()
            },
            () => {
                this.alarmTonePlayStarting = false
            }
        )
    }

    private alarmAudios(): HTMLMediaElement[] {
        return [this.notification_audio, this.p1_notification_audio].filter(
            (audio): audio is HTMLMediaElement => Boolean(audio)
        )
    }

    private markAlarmAudioUnlocked(audio: HTMLMediaElement) {
        this.unlockedAlarmAudios.add(audio)
    }

    private allAlarmAudiosUnlocked(audios: HTMLMediaElement[] = this.alarmAudios()): boolean {
        return audios.length > 0 && audios.every((audio) => this.unlockedAlarmAudios.has(audio))
    }

    private refreshSoundAutoplayBlockedForUnlockState() {
        const blocked = !this.allAlarmAudiosUnlocked()
        if (this.state.soundAutoplayBlocked !== blocked) {
            this.setState({soundAutoplayBlocked: blocked})
        }
        if (blocked) {
            this.addSoundUnlockHandler()
        } else {
            this.removeSoundUnlockHandler()
        }
    }

    private invalidateSoundUnlockAttempt(){
        this.soundUnlockAttemptId += 1
    }

    private playPendingNewArrivalAnnouncement(){
        if (this.pendingNewArrivalAnnouncements.size === 0) {
            return
        }

        const pendingIds = new Array<string>()
        let priority = 100
        let reanchor = false
        this.pendingNewArrivalAnnouncements.forEach((announcement, id) => {
            pendingIds.push(id)
            if (announcement.priority < priority) {
                priority = announcement.priority
                reanchor = announcement.reanchor
            } else if (announcement.priority === priority) {
                reanchor = reanchor || announcement.reanchor
            }
        })

        this.playAlarmTone(priority, () => {
            pendingIds.forEach((id) => { this.pendingNewArrivalAnnouncements.delete(id) })
            if (reanchor) {
                this.register_timer("persistence", () => { this.persistenceTick() }, this.soundInterval)
            }
        })
    }

    // The warning follows per-audio unlock confirmation. userActivation is only
    // a fallback for environments where the audio elements are absent.
    private refreshSoundAutoplayBlocked(){
        // userActivation isn't in this project's DOM lib (TS 4.5), so read it via
        // a narrow cast. It may also be undefined in older browsers at runtime.
        const userActivation = (navigator as Navigator & {
            userActivation?: {hasBeenActive: boolean}
        }).userActivation
        if (userActivation === undefined) {
            return
        }
        const audios = this.alarmAudios()
        const blocked = audios.length > 0 ? !this.allAlarmAudiosUnlocked(audios) : !userActivation.hasBeenActive
        if (this.soundUnlockExplicitlyFailed && !blocked) {
            return
        }
        if (this.state.soundAutoplayBlocked !== blocked) {
            this.setState({soundAutoplayBlocked: blocked})
        }
    }

    private async unlockAlarmAudio(){
        if (this.soundUnlockInProgress) {
            return
        }
        const audios = this.alarmAudios()
        if (!audios.length) {
            return
        }

        this.interruptedPrimeAudios.clear()
        this.soundUnlockExplicitlyFailed = false
        this.soundUnlockInProgress = true
        const unlockAttemptId = ++this.soundUnlockAttemptId
        let primeFailed = false
        try {
            for (const audio of audios) {
                if (this.unlockedAlarmAudios.has(audio)) {
                    continue
                }
                if (unlockAttemptId !== this.soundUnlockAttemptId) {
                    break
                }
                if (!this.mounted || !this.pageVisible) {
                    primeFailed = true
                    break
                }
                if (this.alarmAudioIsActive(audios)) {
                    this.invalidateSoundUnlockAttempt()
                    break
                }
                const unlocked = await this.primeAudioElement(audio)
                if (unlockAttemptId !== this.soundUnlockAttemptId) {
                    break
                }
                if (unlocked) {
                    this.markAlarmAudioUnlocked(audio)
                } else {
                    this.unlockedAlarmAudios.delete(audio)
                    primeFailed = true
                    break
                }
            }
        } finally {
            this.soundUnlockInProgress = false
        }

        if (!this.mounted || !this.pageVisible) {
            return
        }
        if (unlockAttemptId !== this.soundUnlockAttemptId) {
            return
        }
        if (!primeFailed && this.allAlarmAudiosUnlocked(audios)) {
            this.soundUnlockExplicitlyFailed = false
            this.setState({soundAutoplayBlocked: false})
            this.removeSoundUnlockHandler()
        } else {
            this.soundUnlockExplicitlyFailed = true
            this.setState({soundAutoplayBlocked: true})
            this.addSoundUnlockHandler()
        }
    }

    private alarmAudioIsActive(audios: HTMLMediaElement[]): boolean {
        return this.alarmTonePlayStarting || audios.some((audio) => !audio.paused)
    }

    private primeAudioElement(audio: HTMLMediaElement): Promise<boolean> {
        if (!audio.paused) {
            return Promise.resolve(true)
        }
        const originalMuted = audio.muted
        const originalVolume = audio.volume
        const originalTime = audio.currentTime
        audio.muted = false
        audio.volume = 0
        if (audio.volume !== 0) {
            audio.volume = originalVolume
            audio.muted = originalMuted
            console.warn("Unable to silently unlock alarm audio because volume control is not supported.")
            return Promise.resolve(false)
        }

        const restore = () => {
            if (this.interruptedPrimeAudios.has(audio)) {
                this.interruptedPrimeAudios.delete(audio)
                return
            }
            audio.pause()
            audio.volume = originalVolume
            audio.muted = originalMuted
            try {
                audio.currentTime = originalTime
            } catch (error) {
                console.warn("Unable to reset alarm audio after sound unlock.", error)
            }
        }

        let playPromise: Promise<void> | undefined
        try {
            playPromise = audio.play()
        } catch (error) {
            restore()
            console.warn("Unable to unlock alarm audio. User interaction is required.", error)
            return Promise.resolve(false)
        }
        if (playPromise === undefined) {
            return new Promise<boolean>((resolve) => {
                let timeout: number
                let onPlaying: () => void
                let onError: (error: Event) => void
                const cleanup = () => {
                    window.clearTimeout(timeout)
                    audio.removeEventListener("playing", onPlaying)
                    audio.removeEventListener("error", onError)
                }
                onPlaying = () => {
                    cleanup()
                    restore()
                    resolve(true)
                }
                onError = (error: Event) => {
                    cleanup()
                    restore()
                    console.warn("Unable to unlock alarm audio. User interaction is required.", error)
                    resolve(false)
                }
                timeout = window.setTimeout(() => {
                    cleanup()
                    restore()
                    console.warn("Unable to confirm alarm audio unlock. User interaction is required.")
                    resolve(false)
                }, 3000)
                audio.addEventListener("playing", onPlaying)
                audio.addEventListener("error", onError)
            })
        }

        return playPromise.then(
            () => {
                restore()
                return true
            },
            (error) => {
                restore()
                console.warn("Unable to unlock alarm audio. User interaction is required.", error)
                return false
            }
        )
    }

    private getHideResidentName(parameters: URLSearchParams) : boolean {
        // We use the read_permissions as a sort of user default - e.g. a permission
        // indicates we must do something.
        //
        // If we don't have a query param, and we have a permission, then we default to show,
        // otherwise the query_param controls.
        const permission_present = eeviGlobal.loggedInUser.read_permissions.has("hide_resident_name");
        const queryParamValue = parameters.get("hideResidentName")
        if (queryParamValue == undefined && permission_present){
            return true;
        }
        return this.convertStringToBoolean(parameters.get("hideResidentName") || "")
    }

    private convertStringToBoolean(value: string) : boolean {
        const truthy: string[] = [
            'y',
            'yes',
            't',
            'true',
            'True',
            '1'
        ]
        return truthy.includes(value)
    }

    private getSortFieldName(parameters: URLSearchParams) : string {
        // We use the read_permissions as a sort of user default - e.g. a permission
        // indicates we must do something.
        //
        // If we don't have a query param, and we have a permission, then we default to triggered,
        // otherwise the query_param controls.
        const permission_present = eeviGlobal.loggedInUser.read_permissions.has("sort_field_trigger_time");
        const queryParamValue = parameters.get("sortField")
        if (queryParamValue == undefined && permission_present){
            return "triggered_time";
        }
        return this.convertStringToSortFieldName(queryParamValue || "");
    }

    private convertStringToSortFieldName(value: string) : string {
        const validFieldNames: string[] = [
            'default',
            'triggered_time',
            'location',
            'resident_full_name',
            'arrived_carer'
        ]

        if (validFieldNames.includes(value)) {
            return value
        }
        else {
            return 'default'
        }
    }

    private getSortOrderAscending(parameters: URLSearchParams) : boolean {
        // We use the read_permissions as a sort of user default - e.g. a permission
        // indicates we must do something.
        //
        // If we don't have a query param, and we have a permission, then we default to triggered,
        // otherwise the query_param controls.
        const permission_present = eeviGlobal.loggedInUser.read_permissions.has("sort_order_desc");
        const queryParamValue = parameters.get("sortOrder")
        if (queryParamValue == undefined && permission_present){
            // Descending as we have permission and no query value
            return false;
        }

        return this.convertStringToSortOrderAscending(queryParamValue || "");
    }

    private convertStringToSortOrderAscending(value: string) : boolean {
        const descendingValues: string[] = [
            'desc'
        ]
        if (descendingValues.includes(value)) {
            return false;
        }
        return true;
    }

    protected onFormDidMount() {
        this.mounted = true;

        // Setting up a global counter that tick every 1s to simulate a clock
        this.register_timer("clock",()=>{this.setState({currentDateTime: new Date()})}, 1000);

        let parameters = new URLSearchParams(window.location.search)
        this.demoMode = parseInt(parameters.get("demo") || "") || undefined

        // Load careGroups into EeviCoreContext if necessary
        this.loadCareGroups()

        // On TV displays the first real remote/key/click event is the best
        // chance to unlock alarm audio before a real event needs to sound.
        this.addSoundUnlockHandler()

        // Reflect the current sound-unlock state in the UI before the first
        // poll; later playback and prime attempts confirm each audio element.
        this.refreshSoundAutoplayBlocked()

        this.initialize()
    }

    private topUpWithRecentEvents(recentPage: Partial<AlarmsFormState>): Partial<AlarmsFormState> {
        const oldEvents = this.state.events.filter(e => e.active);
        const newEvents = new Array<RealtimeEvent>();

        for (let event of recentPage.events!) {
            event = dynamicSubclass(event, RealtimeEventCompute)
            let found = false
            // Find and update open events
            for (const existingEvent of oldEvents){
                if (event.eventId === existingEvent.eventId){
                    found = true
                    Object.assign(existingEvent, event);
                    break;
                }
            }

            if (!found){
                if (event.active && !event.isTechSupport){
                    newEvents.push(event);
                }
            }
        }

        const activeEvents = [...newEvents, ...oldEvents].filter((e)=>e.withinQueryWindow);

        // Apply what ever sort order we have defined
        let sorted_active_events: RealtimeEvent[]
        if (this.state.sortField == 'triggered_time')
        {
            // Event triggeredTime sorted in ascending order (the earliest event first)
            sorted_active_events = activeEvents.sort(
                (a,b)=>{
                    return Date.parse(a.triggeredTime) - Date.parse(b.triggeredTime);
                }
            )
        }
        else if (this.state.sortField == 'location')
        {
            // Event triggeredTime sorted in ascending order (the earliest event first)
            sorted_active_events = activeEvents.sort(
                (a,b)=>{
                    return a.location < b.location ? -1 : 1
                }
            )
        }
        else if (this.state.sortField == 'resident_full_name')
        {
            // Event triggeredTime sorted in ascending order (the earliest event first)
            sorted_active_events = activeEvents.sort(
                (a,b)=>{
                    return a.residentFullName < b.residentFullName ? -1 : 1
                }
            )
        }
        else if (this.state.sortField == 'arrived_carer')
        {
            // Event triggeredTime sorted in ascending order (the earliest event first)
            sorted_active_events = activeEvents.sort(
                (a,b)=>{
                    return (a.arrivedCarer || '')  < (b.arrivedCarer || '') ? -1 : 1
                }
            )
        }

        else
        {
            // Default sort order, which is by open then taken
            sorted_active_events = activeEvents.sort(
                (a,b)=>{return a.status - b.status}
            );
        }

        // If descending then flip order
        if (!this.state.sortAscendingOrder)
        {
            sorted_active_events = sorted_active_events.reverse();
        }

        // --- Audible alerts: diff this tick's unattended events against the
        // tracked set to find genuinely new arrivals, and prune resolved ones. ---
        const currentUnattended = activeEvents.filter((e) => e.isOpen && !e.isTaken)
        const currentIds = new Set(currentUnattended.map((e) => e.eventId))

        // 1) Out: drop events no longer unattended (taken / completed / gone).
        this.tracked.forEach((_priority, id) => {
            if (!currentIds.has(id)) { this.tracked.delete(id) }
        })
        this.pendingNewArrivalAnnouncements.forEach((_announcement, id) => {
            if (!currentIds.has(id)) { this.pendingNewArrivalAnnouncements.delete(id) }
        })

        // 2) Highest priority among events that were already active (still tracked
        // after pruning, before adding new arrivals) - drives the re-anchor test.
        let alreadyActivePriority = 100
        this.tracked.forEach((priority) => {
            alreadyActivePriority = Math.min(alreadyActivePriority, priority)
        })

        // 3) In: unattended events not yet tracked are this tick's new arrivals.
        const newArrivals = currentUnattended.filter((e) => !this.tracked.has(e.eventId))
        newArrivals.forEach((e) => {
            this.tracked.set(e.eventId, e.priority)
            this.pendingNewArrivalAnnouncements.set(e.eventId, {
                priority: e.priority,
                reanchor: e.priority <= alreadyActivePriority,
            })
        })

        return {
            ...recentPage,
            events: sorted_active_events
        };
    }

    private retrieveRecentEvents(showLoadingScreen: boolean = false) {
        const parameters = this.getParameters();
        let retrievedEvents: Partial<AlarmsFormState> = {} //Intermediate variable to store response result

        function recursive_polling(resp: Partial<AlarmsFormState>, api_client:EeviApi<AlarmsFormState>){
            // Recursively polling data with the requested parameters until response.nextToken is empty
            if (resp.nextToken){
                const url = `${apiV1}/events_status?nextToken=${encodeURIComponent(resp.nextToken)}&` + parameters;
                api_client.get(
                    url, undefined, (resp: Partial<AlarmsFormState>) => recursive_polling(resp, api_client)
                )
            }
            retrievedEvents = {
                ...resp,
                events: [
                    ...retrievedEvents.events || [],
                    ...resp.events || []
                ]
            }
        }



        this.api.get(
            `${apiV1}/events_status?` + parameters,
            undefined,
            (data: Partial<AlarmsFormState>) => {
                if (data.requestId === this.requestId) {
                    let parameters = new URLSearchParams(window.location.search)

                    //Successfully get data
                    recursive_polling(data, this.api)

                    if (this.demoMode) {
                        // @ts-ignore
                        this.setState({
                            ...this.topUpWithRecentEvents(getDemoEvents(this.demoMode)),
                            loading: false,
                            error: null,
                            lastUpdate: this.state.currentDateTime,

                            hideResidentName: this.getHideResidentName(parameters),
                            sortField: this.getSortFieldName(parameters),
                            sortAscendingOrder: this.getSortOrderAscending(parameters),
                        });
                    } else {
                        this.setState({
                            ...this.state,
                            ...this.topUpWithRecentEvents(retrievedEvents),
                            loading: false,
                            error: null,
                            lastUpdate: this.state.currentDateTime,

                            hideResidentName: this.getHideResidentName(parameters),
                            sortField: this.getSortFieldName(parameters),
                            sortAscendingOrder: this.getSortOrderAscending(parameters),
                        });
                    }

                    // Keep the "audio blocked" warning in sync with whether the
                    // user has interacted yet, so it clears once a real alarm can
                    // be heard - without waiting for an alarm to occur.
                    this.refreshSoundAutoplayBlocked()

                    // New-arrival announcement: retry every successful event poll
                    // until the browser allows the tone to play. Re-anchor the
                    // 60s reminder only after an audible announcement.
                    if (this.pageVisible) {
                        this.playPendingNewArrivalAnnouncement()
                    }
                }
            },
            undefined,
            showLoadingScreen,
            (error) => {
                this.eventStatusErrorHandler(error)
            }
        );

    }

    private eventStatusErrorHandler(error: Partial<any>){
        this.errorHandler(error)
    }

    private isHandledTransientApiError(error: Partial<any>): boolean {
        return Boolean(error.response && (error.response.status === 503 || error.response.status === 429))
    }


    private getParameters(): string {
        this.requestId = eeviId()
        const endDate = this.state.currentDateTime;
        const startDate = new Date(endDate.getTime() - QUERY_WINDOW);
        return `endDate=${encodeURIComponent(endDate.toISOString())}` +
            `&startDate=${encodeURIComponent(startDate.toISOString())}` +
            `&requestId=${this.requestId}`;
    }

    protected onFormWillUnmount() {
        this.mounted = false;
        this.removeVisibilityHandler()
        this.removeSoundUnlockHandler()
        this.pageVisible = false;
        this._timers.forEach(clearInterval);
        this.clear_timers();
    }

    protected renderForm(): React.ReactNode {
        let content: JSX.Element
        if (this.state.error){
            content = <div className="text-container">{
                'The Event Status Page is down for maintenance.\n' +
                'For more information please contact support@eevi.life or 1300 802 738.'
            }</div>
        } else {
            content = <AlarmTable
                zoneLocations={this.state.zoneLocations} selectedZones={this.state.selectedZones}
                activeEvents={this.state.events} availableCarers={this.state.availableCarers}
                hideResidentDetails={this.state.hideResidentName} />
        }

        return <div className="AlarmPage">
            <AlarmHeader
                zoneLocations={this.state.zoneLocations} selectedZones={this.state.selectedZones}
                setSelectedZones={this.handleSelectedZonesChange}
                soundAutoplayBlocked={this.state.soundAutoplayBlocked}
                now={this.state.currentDateTime} lastUpdate={this.state.lastUpdate} />
            {content}
        </div>;
    }

    /**
     * Custom API error handler for event_status page.
     * @private
     */
    private errorHandler(error: Partial<any>){
        // ATM, the way an api error propagates is:
        // -> The default action is EeviApi method will set the current form's state {error:any},
        // -> There is any option of providing an errorCallback function in EeviApi to handle the error
        // -> If not suppressed, the EeviError tag will render this as a generic global error

        if (this.isHandledTransientApiError(error)) {
            // Warning: 503 error means different things in different pages
            // in this page, it is usually mean "Service Unavailable"
            // -> nothing to handle here, simply keep polling
            console.log(`API Error: ${error.message}`)
            error.response.data.errors[0].handled = true
        }
        this.setState({...this.state, loading: false, error: error});
    }

    render(): React.ReactNode{
        return <EeviBaseContainer
            title={"Alarms"}
            formState={this.state}
            login={(c:LoginCredentials) => super.login(c)}
            addLoginRegisterButton={false}>
            {this.renderForm()}
        </EeviBaseContainer>;
    }

}

export default withRouter(AlarmsForm);
